/**
 * AdUnit - Reusable ad component for Toolmetrico
 *
 * Renders two ad placements per usage:
 *   1. Native ad (profitablecpmratenetwork)
 *   2. Banner ad 728x90 (highperformanceformat)
 *
 * Utilizes encapsulated srcDoc iframes to guarantee zero DOM ID collisions,
 * zero global window.atOptions race conditions, and 100% reliable ad delivery
 * even when rendered multiple times on the same page.
 */

import React from 'react';

const NATIVE_SCRIPT_SRC =
  'https://pl29437388.profitablecpmratenetwork.com/1275e3e5c96753a00589106eea8df4b2/invoke.js';
const NATIVE_CONTAINER_ID = 'container-1275e3e5c96753a00589106eea8df4b2';

const BANNER_SCRIPT_SRC =
  'https://www.highperformanceformat.com/54b7aea6966a4019ae8c4d79d6e0a9e6/invoke.js';
const BANNER_KEY = '54b7aea6966a4019ae8c4d79d6e0a9e6';

export const AdUnit = () => {
  const nativeSrcDoc = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <style>
          body {
            margin: 0;
            padding: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            background: transparent;
            font-family: sans-serif;
          }
          #${NATIVE_CONTAINER_ID} {
            width: 100%;
            max-width: 900px;
            display: flex;
            justify-content: center;
          }
        </style>
      </head>
      <body>
        <div id="${NATIVE_CONTAINER_ID}"></div>
        <script async="async" data-cfasync="false" src="${NATIVE_SCRIPT_SRC}"></script>
      </body>
    </html>
  `;

  const bannerSrcDoc = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <style>
          body {
            margin: 0;
            padding: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            background: transparent;
            overflow: hidden;
          }
        </style>
      </head>
      <body>
        <script>
          window.atOptions = {
            'key' : '${BANNER_KEY}',
            'format' : 'iframe',
            'height' : 90,
            'width' : 728,
            'params' : {}
          };
        </script>
        <script src="${BANNER_SCRIPT_SRC}"></script>
      </body>
    </html>
  `;

  return (
    <div
      aria-label="Advertisement"
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '20px',
        margin: '32px 0 8px',
        padding: '0 0 4px',
      }}
    >
      {/* ── Encapsulated Native Ad ────────────────────────────────────────────── */}
      <div
        style={{
          width: '100%',
          maxWidth: '900px',
          minHeight: '260px',
          borderRadius: '16px',
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          background: 'transparent',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)',
        }}
      >
        <iframe
          title="Native Sponsored Content"
          srcDoc={nativeSrcDoc}
          style={{
            width: '100%',
            height: '280px',
            border: 'none',
            background: 'transparent',
            overflow: 'hidden',
          }}
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        />
      </div>

      {/* ── Encapsulated Banner Ad 728×90 ─────────────────────────────────────── */}
      <div
        style={{
          width: '100%',
          maxWidth: '728px',
          height: '90px',
          overflow: 'hidden',
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          margin: '0 auto',
          background: 'transparent',
        }}
      >
        <iframe
          title="Leaderboard Banner Ad"
          srcDoc={bannerSrcDoc}
          style={{
            width: '100%',
            maxWidth: '728px',
            height: '90px',
            border: 'none',
            background: 'transparent',
            overflow: 'hidden',
          }}
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        />
      </div>

      <span
        style={{
          fontSize: '0.65rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
          opacity: 0.55,
          userSelect: 'none',
        }}
      >
        Advertisement
      </span>
    </div>
  );
};

export default AdUnit;
